import { ChangeEvent, Suspense, lazy, useCallback, useState } from 'react';
import Header from 'components/Header/Header';
import { ThemeProvider } from 'styled-components';
import theme from 'assets/styles/theme';
import { isEmpty } from 'lodash';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import GlobalStyle from 'assets/styles/globalStyles';
import { selecZoom } from 'store/features/zoom/zoomSlice';
import { Modals } from 'components/Dialogs';
import { ModalType } from 'components/Dialogs/types';
import { EntityId } from '@reduxjs/toolkit';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import UploadModal from 'components/Dialogs/UploadModal/UploadModal';
import DragDropFileUpload from 'components/DragDropFileUpload/DragDropFileUpload';
import AddFileButton from 'components/AddFileButton/AddFileButton';
import EmptyBox from 'Views/Empty';
import { addFilesInputHandler } from 'helpers';
import { selectFiles, selectFilesIds } from 'store/features/files/selectors';
import { PAGE_CONTAINER_ID } from 'const';
import { FilesWrapper, Wrapper } from 'components/FilesWrapper';
import { selectPages } from 'store/features/pages/selectors';

const PDFViewer = lazy(() =>
    import('../components/PDFViewer/PDFViewer').then(module => ({ default: module.PDFViewer }))
);
const FileViewer = lazy(() =>
    import('components/FileViewer/FileViewer').then(module => ({ default: module.FileViewer }))
);

const AppComponent = () => {
    const dispatch = useAppDispatch();
    const [isOpenPagePreview] = useState(false);
    const [actionStatus, setActionStatus] = useState<ModalType>(null);
    const [actualPage, setActualPage] = useState<EntityId>('');
    const [showRemovedPages, setShowRemovedPages] = useState<boolean>(false);

    const toggleShowRemoved = () => {
        setShowRemovedPages(prevValue => !prevValue);
    };

    const toggleModalOpen = useCallback((type: ModalType, id?: EntityId) => {
        setActionStatus(type);
        if (id) {
            setActualPage(id);
        }
    }, []);

    const files = useAppSelector(selectFiles);
    const pages = useAppSelector(selectPages);
    const filesIds = useAppSelector(selectFilesIds);
    const zoomValue = useAppSelector(selecZoom);

    return (
        <ThemeProvider theme={theme}>
            <GlobalStyle />
            <Header toggleModalOpen={toggleModalOpen} />
            <Wrapper>
                <UploadModal />
                <Modals
                    type={actionStatus}
                    toggleModalOpen={toggleModalOpen}
                    actualPageId={actualPage}
                    showRemoved={showRemovedPages}
                    toggleShowRemoved={toggleShowRemoved}
                />
                {isEmpty(filesIds) ? (
                    <EmptyBox />
                ) : (
                    <>
                        <DragDropFileUpload addFileInputHandler={addFilesInputHandler}>
                            <FilesWrapper isModalOpen={isOpenPagePreview} zoom={zoomValue} id={PAGE_CONTAINER_ID}>
                                <AddFileButton
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        addFilesInputHandler(e.target.files, dispatch, e)
                                    }
                                />
                            </FilesWrapper>
                        </DragDropFileUpload>
                        <DndProvider backend={HTML5Backend} context={window}>
                            {filesIds.map(id => {
                                const pageId = files[id]!.pagesIds[0];
                                const fileType = pages.entities[pageId]?.type;
                                if (fileType === 'PDF') {
                                    return (
                                        <Suspense key={id}>
                                            <PDFViewer id={id} toggleModalOpen={toggleModalOpen} />
                                        </Suspense>
                                    );
                                } else {
                                    return (
                                        <Suspense key={id}>
                                            <FileViewer id={id} toggleModalOpen={toggleModalOpen} />
                                        </Suspense>
                                    );
                                }
                            })}
                        </DndProvider>
                    </>
                )}
            </Wrapper>
        </ThemeProvider>
    );
};

export default AppComponent;
